import { toast } from "@heroui/react";
import { FormattedMessage } from "react-intl";
import React, { useCallback } from "react";

/**
 * Wraps a save action in a SINGLE toast that transitions loading → success/error.
 *
 * Uses HeroUI's `toast.promise` so one save produces one toast (no separate "in progress" +
 * success/error cascade). The returned promise keeps the historical contract relied on by ~60 call
 * sites: it resolves to the save result on success and to `undefined` on failure - it never rejects,
 * so `toastSaver(fn).then(...)` chains stay safe.
 */
export const useToastSaver = () => {
    // `Promise<any>` preserves the historical loose contract relied on by ~60 call sites that read
    // `toastSaver(fn).then((res) => res.data...)`; tightening it is out of scope for the toast change.
    return useCallback((saveFn: () => Promise<any>, successMessage?: (data: any) => React.ReactNode): Promise<any> => {
        const promise = saveFn();
        toast.promise(promise, {
            loading: (<FormattedMessage id="app.message.progress" />) as React.ReactNode,
            // successMessage lets a caller report something save-result-specific (e.g. a count)
            // through the SAME toast.promise, instead of a second toast after it.
            success: successMessage ?? ((<FormattedMessage id="app.message.success" />) as React.ReactNode),
            error: (e: Error) => e.message,
        });
        // Swallow the rejection into `undefined` so the chain never rejects; the toast above already
        // surfaced the error. toast.promise has its own handler, so this is a second, independent one.
        return promise.catch((): undefined => undefined);
    }, []);
};
